"use client";

import { useEffect } from "react";
import { useToast } from "@dionatanbig2be/big2be-ui";
import { __setToastApi } from "./toast";

/**
 * Seletor do viewport de toasts montado pelo big2be-ui — o mesmo que o
 * `globals.css` usa para o z-index e a geometria mobile.
 */
const VIEWPORT = '[data-component="ToastProvider"][data-slot="viewport"]';

/**
 * Sobe o viewport de toasts para a TOP LAYER.
 *
 * Sem isto o aviso some atrás de qualquer modal: o `Modal` da lib abre com
 * `dialog.showModal()`, e dialog modal é pintado na top layer — uma camada
 * acima da árvore normal, onde z-index não alcança (um toast com z 10000
 * continua embaixo de um dialog sem z nenhum; é por especificação). Medido no
 * modal de login: com o dialog aberto, o ponto sobre o toast devolvia o
 * DIALOG.
 *
 * `popover="manual"` coloca o elemento na mesma camada sem torná-lo modal (não
 * fecha com Esc nem com clique fora). A ordem dentro da top layer é a de
 * entrada, por isso re-exibimos a cada aviso: um toast disparado com o modal
 * já aberto entra depois dele e fica por cima.
 *
 * PONTE: o `ToastProvider` publicado (2.6.7) não conhece popover; a versão nova
 * da lib já faz isso sozinha. Enquanto a loja consome o pacote, quem promove é
 * este observer — quando a lib subir, ele vira no-op (o atributo já vem posto e
 * o elemento já estará aberto).
 */
function useTopLayerToasts() {
    useEffect(() => {
        const promote = () => {
            const el = document.querySelector<HTMLElement>(VIEWPORT);
            if (!el || typeof el.showPopover !== "function") return;
            if (el.getAttribute("popover") !== "manual") el.setAttribute("popover", "manual");

            try {
                if (el.matches(":popover-open")) el.hidePopover();
                el.showPopover();
            } catch {
                // popover indisponível/bloqueado — segue com o z-index do CSS
            }
        };

        // O viewport é filho direto do <body> (o provider mora no root layout),
        // então `childList` sem `subtree` já pega o momento em que ele aparece —
        // e não paga o custo de observar a árvore inteira a cada render.
        const onBody = new MutationObserver(promote);
        onBody.observe(document.body, { childList: true });

        // Aviso novo com o viewport JÁ montado não mexe no body: quem muda é a
        // lista de dentro. Sem observar isso, o 2º toast não re-entra na top
        // layer e ficaria atrás de um modal aberto no meio do caminho.
        let onList: MutationObserver | null = null;
        const watchList = () => {
            const el = document.querySelector(VIEWPORT);
            onList?.disconnect();
            onList = null;
            if (!el) return;
            onList = new MutationObserver(promote);
            onList.observe(el, { childList: true });
        };

        const onBodyAndList = new MutationObserver(watchList);
        onBodyAndList.observe(document.body, { childList: true });

        promote();
        watchList();

        return () => {
            onBody.disconnect();
            onBodyAndList.disconnect();
            onList?.disconnect();
        };
    }, []);
}

/** Captura a api do ToastProvider para a ponte global `toast`. Montar UMA vez, dentro do ToastProvider. */
export function ToastBridge() {
    const toastApi = useToast();

    useTopLayerToasts();

    useEffect(() => {
        __setToastApi(toastApi);
        return () => __setToastApi(null);
    }, [toastApi]);

    return null;
}
